// SPDX-License-Identifier: GPL-3.0-only
// OpenSaga: aisys.cpp GoToOrigin/GoToLocator; gizmos_gizactions.cpp FollowPlayer;
// gameaiscript.cpp EngageOpponent; character.cpp GCDATA_DEFAULT; game_obj.cpp activation.
import { EnemyScriptRuntime, SourceEngagement, insideAITrigger } from './enemies.mjs';

const position = actor => actor?.collisionPosition ?? (actor?.root?.position ? [actor.root.position.x, actor.root.position.y, actor.root.position.z] : actor?.position);
const distance2 = (a, b) => a.reduce((sum, v, i) => sum + (v - b[i]) ** 2, 0);
const number = (value, fallback) => Number.isFinite(value) ? value : fallback;
const argumentNumber = (args, key, fallback) => {
  const text = args.find(v => v.toLowerCase().startsWith(`${key}=`));
  if (!text) return fallback;
  const result = Number(text.slice(key.length + 1));
  if (!Number.isFinite(result) || result < 0) throw new Error(`Invalid original ${key}`);
  return result;
};

// This adapter implements a single-player ordinary-enemy subset. Navigation
// is explicitly an unobstructed-segment adapter, NOT the native path solver.
// Unknown LOS/ground results prevent physical movement. onFire requests a
// weapon action: projectile physics and damage belong to the caller.
export function createEnemyController({ ai, actors, scripts, player, difficulty,
  messages = new Map(), forceComplete = () => undefined, obstacleAtEnd = () => undefined,
  partyUnderCover = () => false, getFloor, getLineOfSight, onFire = () => {}, random = Math.random,
  canAttack = () => true, partyMemberInWay = () => false } = {}) {
  if (!ai || !Array.isArray(actors) || !(scripts instanceof Map) || typeof player !== 'function' ||
      !Number.isInteger(difficulty) || difficulty < 0 || difficulty > 255) throw new Error('Invalid original enemy controller inputs');
  const report = { limitations: ['Single-player target selection only; native ally/group awareness propagation is not implemented.',
    'Movement follows only caller-confirmed unobstructed segments; native routing, obstacle negotiation, jump links and acceleration are not implemented.',
    'Respawn groups, type-2 activation scripts and staggered activation are not implemented.',
    'Caller supplies party-in-way and attack arbitration; their default adapters represent an unobstructed single opponent.',
    'Root positions are used when collisionPosition is unavailable; original collision-height and visibility extensions require caller fields.'], blocked: [] };
  const records = [], seen = new Set();
  let disposed = false;
  const note = (id, reason) => { const key = `${id}:${reason}`; if (!seen.has(key)) { seen.add(key); report.blocked.push({ id, reason }); } };
  const areaByName = name => ai.areas.find(a => a.name === name?.toLowerCase());
  const locatorByName = name => ai.locators.find(a => a.name === name?.toLowerCase());
  const queryArea = (name, target) => {
    // Condition_EitherPlayerInTriggerArea falls back to processor->unknown_a0;
    // if the authored default is also NULL it returns0, not unknown.
    if (name === undefined || name === null || name === '') return false;
    const area = areaByName(name), p = position(target);
    return !area ? undefined : p ? insideAITrigger(area, p) : false;
  };

  for (const { spawn, actor } of actors) {
    const config = actor.config?.values;
    if (!spawn || !actor.root || !config || !Array.isArray(spawn.origin)) throw new Error('Invalid authored enemy actor');
    const record = { spawn, actor, active: spawn.runtimeState === 'active', aware: false, side: config.baddie ? 'baddie' : config.neutral ? 'neutral' : 'goodie',
      invulnerable: false, overrides: new Map(), intent: null, speed: 'run', actionData: null, view: spawn.viewDistance || number(config.viewrange, 6),
      hear: spawn.hearDistance || number(config.heardistance, 3), maxHeight: spawn.maxViewHeight || number(config.max_viewheight, .5),
      minHeight: spawn.minViewHeight || number(config.min_viewheight, -.5) };
    const script = scripts.get(spawn.script);
    actor.root.visible = record.active;
    actor.eligibleDamage = record.active && record.side === 'baddie' && !config.untargetable;
    if (!script) note(spawn.id, `Missing original script ${spawn.script}`);
    const getOpponent = () => {
      const target = player();
      return record.aware && record.side === 'baddie' && target && !target.dead && !target.config?.values?.untargetable ? target : null;
    };
    const condition = (name, args) => {
      const target = player(), p = position(actor), t = position(target), opponent = getOpponent(), locator = locatorByName(spawn.locator);
      if (name === 'gotopponent') return !!opponent;
      if (name === 'gotlocator') return !!locator;
      if (name === 'originrange') return Math.sqrt(distance2(p, spawn.origin));
      if (name === 'opponentrange') return opponent ? Math.sqrt(distance2(p, position(opponent))) : 3.402823466e38;
      if (name === 'nearestplayerrange' || name === 'playerrange') return t ? Math.sqrt(distance2(p, t)) : 3.402823466e38;
      if (name === 'locatorrange') return locator ? Math.sqrt(distance2(p, locator.position)) : 3.402823466e38;
      if (name === 'partyundercover') return partyUnderCover();
      if (name === 'eitherplayerintriggerarea') return queryArea(args[0] ?? spawn.area, target);
      if (name === 'forcecomplete') return forceComplete(args[0]);
      if (name === 'obstacleatend') return obstacleAtEnd(args[0]);
      return undefined;
    };
    const action = (name, args, { firstTime, dt }) => {
      if (firstTime) { record.actionData = null; record.speed = 'run'; }
      const lower = args.map(v => v.toLowerCase());
      for (const mode of ['run', 'walk', 'tiptoe']) if (lower.includes(mode)) record.speed = mode;
      if (name === 'setside') {
        if (!['neutral', 'baddie', 'goodie'].includes(lower[0])) return undefined;
        record.side = lower[0]; if (record.side !== 'baddie') record.aware = false; return true;
      }
      if (name === 'dontpush') { actor.dontPush = !lower.includes('false'); return true; }
      if (name === 'setinvulnerable') { record.invulnerable = !lower.includes('false'); return true; }
      if (name === 'overrideanimation') {
        if (lower.includes('reset')) { record.overrides.clear(); return true; }
        const from = lower.find(v => v.startsWith('from='))?.slice(5), to = lower.find(v => v.startsWith('to='))?.slice(3);
        if (!from || !to || !actor.animations?.has(to)) return undefined;
        record.overrides.set(from, to); return true;
      }
      if (name === 'kill') { actor.dead = true; actor.root.visible = false; return true; }
      if (name === 'idle') return false;
      if (name === 'followplayer') {
        const target = player(); if (!target || target.dead) return false;
        const unsupported = lower.filter(v => !['run', 'walk', 'tiptoe', 'ignore_radius', 'can_go_off_path'].includes(v) && !Number.isFinite(Number(v)));
        if (unsupported.length) return undefined;
        const parameter = number(Number(args.find(v => Number.isFinite(Number(v)))), 0);
        const radius = lower.includes('ignore_radius') ? 0 : number(target.moverRadius, target.config?.values?.radius);
        if (!Number.isFinite(radius)) return undefined;
        record.intent = { target: position(target), range: radius + parameter, direction: 1 }; return false;
      }
      if (name === 'gotoorigin' || name === 'gotolocator') {
        if (spawn.path?.pathIndex >= ai.paths.length || !ai.paths[spawn.path?.pathIndex]?.connections[spawn.path?.connectionIndex]) return true;
        const locator = name === 'gotolocator' ? locatorByName(spawn.locator) : null;
        if (name === 'gotolocator' && !locator) return true;
        // Unsupported naming/random/jump options must not silently change the authored goal.
        if (lower.some(v => !['run', 'walk', 'tiptoe', 'xz_rangecheck', 'ignore_path'].includes(v) && !/^(waittime|goalrange|wait)=/.test(v))) return undefined;
        // Authored Chatting says wait=.5, but source recognizes waittime only;
        // its numeric fallback converts the leading non-number to zero.
        if (firstTime) record.actionData = { wait: argumentNumber(args, 'waittime', .01) || .01, range: argumentNumber(args, 'goalrange', name === 'gotoorigin' ? .2 : 0) };
        const goal = locator?.position ?? spawn.origin, data = record.actionData;
        record.intent = { target: goal, range: data.range, direction: 1 };
        const p = position(actor), dist = lower.includes('xz_rangecheck') ? (p[0] - goal[0]) ** 2 + (p[2] - goal[2]) ** 2 : distance2(p, goal);
        if (!firstTime && dist < (data.range + .1 + dt * record.velocity) ** 2) {
          record.intent = null;
          if (data.wait <= 0) return true;
          data.wait = Math.max(0, data.wait - dt);
        }
        return false;
      }
      if (name === 'engageopponent') {
        const target = getOpponent(); if (!target) return false;
        if (firstTime) record.actionData = new SourceEngagement(args, { random, viewDistance: record.view, blaster: !!config.blaster });
        const result = record.actionData.tick(dt, { distanceSquared: distance2(position(actor), position(target)),
          lineOfSight: getLineOfSight?.(actor, target), drawn: actor.drawn ?? actor.root.visible,
          targetEligible: !target.dead, canAttack: canAttack(actor, target), partyMemberInWay: partyMemberInWay(actor, target) });
        for (const reason of result.blocked) note(spawn.id, `EngageOpponent: ${reason}`);
        if (result.movement) record.intent = { target: position(target), range: result.goalRange, direction: result.movement === 'retreat' ? -1 : result.movement === 'approach' ? 1 : 0 };
        if (result.fireRequested) onFire(actor, target, { action: 'fire', script: record.vm.script.name, state: record.vm.state.name });
        return false;
      }
      return undefined;
    };
    if (script) record.vm = new EnemyScriptRuntime(script, { scripts, messages, condition, action });
    record.velocity = 0; records.push(record);
  }

  function tick(dt) {
    if (disposed) return;
    if (!Number.isFinite(dt) || dt < 0 || dt > 1) throw new Error('Invalid enemy controller timestep');
    const target = player(), targetPosition = position(target);
    for (const r of records) {
      const { spawn, actor } = r;
      if (actor.dead) { actor.eligibleDamage = false; continue; }
      if (!r.active) {
        if (spawn.runtimeState === 'ready' && spawn.activateType === 1 && difficulty >= spawn.activationDifficulty) {
          const trigger = queryArea(spawn.activateArea, target);
          if (trigger === undefined) note(spawn.id, `Missing activation area ${spawn.activateArea}`);
          if (trigger && !spawn.startStagger) { r.active = true; actor.root.visible = true; }
        }
        if (!r.active) { actor.eligibleDamage = false; continue; }
      }
      const p = position(actor), delta = targetPosition?.map((v, i) => v - p[i]);
      if (delta && !target.dead && r.side === 'baddie' && !partyUnderCover()) {
        const dist = distance2(p, targetPosition), extension = number(target.visibilityRangeExtension, 0), radius = r.view + extension;
        if (dist >= radius ** 2) r.aware = false;
        else if (delta[1] - number(target.visibilityHeight, 0) < r.maxHeight && delta[1] + number(target.visibilityHeight, 0) > r.minHeight && getLineOfSight?.(actor, target) === true) {
          const heading = actor.root.rotation?.y ?? spawn.heading;
          const facing = delta[0] * Math.sin(heading) + delta[2] * Math.cos(heading) >= 0;
          if (facing || (target.moving === true && dist < r.hear ** 2)) r.aware = true;
        }
      } else r.aware = false;
      r.intent = null;
      const result = r.vm?.tick(dt);
      if (result) for (const blocked of result.blocked) note(spawn.id, `${blocked.kind}: ${blocked.name}`);
      actor.aiState = r.vm?.state.name;
      actor.eligibleDamage = r.active && r.side === 'baddie' && !r.invulnerable && !actor.config.values.untargetable;
      r.velocity = 0;
      if (r.intent) {
        const goal = r.intent.target, dx = goal[0] - p[0], dz = goal[2] - p[2], length = Math.hypot(dx, dz);
        if (length && actor.root.rotation) actor.root.rotation.y = Math.atan2(dx, dz);
        if (r.intent.direction && (r.intent.direction < 0 || length > r.intent.range)) {
          const speed = actor.config.values[`${r.speed}_speed`];
          if (!Number.isFinite(speed) || speed < 0) { note(spawn.id, `Missing ${r.speed}_speed`); continue; }
          const amount = (r.intent.direction < 0 ? speed * dt : Math.min(speed * dt, Math.max(0, length - r.intent.range))) * r.intent.direction;
          const next = [p[0] + dx / length * amount, p[1], p[2] + dz / length * amount];
          if (getLineOfSight?.(actor, { position: next, movementProbe: true }) === true) {
            const floor = getFloor?.(next[0], next[1], next[2], actor);
            if (Number.isFinite(floor)) { actor.root.position.x = next[0]; actor.root.position.y = floor; actor.root.position.z = next[2]; r.velocity = Math.abs(amount) / (dt || 1); }
            else note(spawn.id, 'Movement has no confirmed terrain surface');
          } else note(spawn.id, 'Movement segment blocked or LOS unavailable');
        }
      }
      const clip = r.velocity ? r.speed : 'idle';
      const override = r.overrides.get(clip) ?? clip;
      if (actor.animations?.has(override)) actor.aiClip = override;
    }
  }
  return { tick, messages, report, records,
    notifyHit(actor) { const record = records.find(r => r.actor === actor); if (record?.side === 'baddie') record.aware = true; },
    dispose() { disposed = true; } };
}
